import type { AxiosResponse, AxiosRequestConfig } from 'axios'

/**
 * API Error class for consistent error handling
 */
export class ApiError extends Error {
  response?: AxiosResponse
  config?: AxiosRequestConfig

  constructor(message: string, response?: AxiosResponse, config?: AxiosRequestConfig) {
    super(message)
    this.name = "ApiError"
    this.response = response
    this.config = config
  }
}

/**
 * Transform an unknown error into an ApiError
 */
export function transformQueryError(error: any): ApiError {
  if (error instanceof ApiError) {
    return error
  }

  if (error?.response) {
    const message = error.response.data?.message || error.response.data?.detail || error.response.statusText || "API Error"
    return new ApiError(message, error.response, error.config)
  }

  if (error?.request) {
    return new ApiError("Network error - please check your connection")
  }

  const message = error?.message || "An unexpected error occurred"
  return new ApiError(message, error?.response, error?.config)
}

/**
 * Error boundary for query functions
 */
export function withErrorHandling<T extends (...args: any[]) => any>(
  queryFn: T,
): (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>> {
  return async (...args: Parameters<T>) => {
    try {
      return await queryFn(...args)
    } catch (error) {
      throw transformQueryError(error)
    }
  }
}

/**
 * Get a user-friendly error message from an error
 */
export function getErrorMessage(error: any): string {
  if (error instanceof ApiError) {
    return error.message
  }

  if (error?.response?.data?.message) {
    return error.response.data.message
  }

  if (error?.response?.data?.detail) {
    return error.response.data.detail
  }

  if (error?.message) {
    return error.message
  }

  return "An unexpected error occurred"
}
